import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Badge } from "react-bootstrap";
import { FaTimes, FaSearch } from "react-icons/fa";
import "./CategoryAutocomplete.css";

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClose]);
}

/** Single-select searchable category field */
export function CategoryAutocomplete({
  label,
  options = [],
  value,
  onChange,
  onBlur,
  error,
  touched,
  placeholder = "Type to search categories...",
  helpText,
  disabled = false,
}) {
  const wrapRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const showError = Boolean(touched && error);

  const selected = useMemo(
    () => options.find((o) => String(o.id) === String(value)) || null,
    [options, value]
  );

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else if (!value) setQuery("");
  }, [selected, value]);

  useOutsideClose(wrapRef, () => setOpen(false));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options
      .filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          o.label.toLowerCase().includes(q) ||
          o.slug?.toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [options, query]);

  return (
    <Form.Group className="mb-3 category-ac" ref={wrapRef}>
      {label && <Form.Label>{label}</Form.Label>}
      <div
        className={`category-ac-control ${disabled ? "disabled" : ""} ${
          showError ? "is-invalid" : ""
        }`}
      >
        <FaSearch className="category-ac-icon" />
        <input
          type="text"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange("");
          }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={onBlur}
          autoComplete="off"
          aria-invalid={showError}
        />
        {value && !disabled && (
          <button
            type="button"
            className="category-ac-clear"
            aria-label="Clear"
            onClick={() => {
              onChange("");
              setQuery("");
              setOpen(false);
            }}
          >
            <FaTimes />
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className="category-ac-menu">
          {filtered.length === 0 ? (
            <div className="category-ac-empty">No matching categories</div>
          ) : (
            filtered.map((opt) => (
              <button
                type="button"
                key={opt.id}
                className={`category-ac-option ${
                  String(opt.id) === String(value) ? "active" : ""
                }`}
                onClick={() => {
                  onChange(String(opt.id));
                  setQuery(opt.label);
                  setOpen(false);
                }}
              >
                <span
                  className="category-ac-name"
                  style={{ paddingLeft: `${(opt.depth || 0) * 14}px` }}
                >
                  {opt.name}
                </span>
                {opt.depth > 0 && opt.path?.length > 1 && (
                  <small className="category-ac-path">
                    {opt.path.slice(0, -1).join(" › ")}
                  </small>
                )}
              </button>
            ))
          )}
        </div>
      )}
      {showError ? (
        <div className="invalid-feedback d-block">{error}</div>
      ) : (
        helpText && <Form.Text>{helpText}</Form.Text>
      )}
    </Form.Group>
  );
}

/** Multi-select searchable category field */
export function CategoryMultiAutocomplete({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Type to search and add...",
  helpText,
  disabled = false,
  emptyMessage = "Select a primary category first",
}) {
  const wrapRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedIds = useMemo(
    () => (value || []).map(String),
    [value]
  );

  const selectedOptions = useMemo(
    () => options.filter((o) => selectedIds.includes(String(o.id))),
    [options, selectedIds]
  );

  useOutsideClose(wrapRef, () => setOpen(false));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = options.filter(
      (o) => !selectedIds.includes(String(o.id))
    );
    if (!q) return available.slice(0, 50);
    return available
      .filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          o.label.toLowerCase().includes(q) ||
          o.slug?.toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [options, query, selectedIds]);

  const add = (id) => {
    const next = [...selectedIds, String(id)];
    onChange(next);
    setQuery("");
    setOpen(true);
  };

  const remove = (id) => {
    onChange(selectedIds.filter((x) => x !== String(id)));
  };

  return (
    <Form.Group className="mb-3 category-ac" ref={wrapRef}>
      {label && <Form.Label>{label}</Form.Label>}

      {selectedOptions.length > 0 && (
        <div className="category-ac-chips">
          {selectedOptions.map((opt) => (
            <Badge key={opt.id} bg="dark" className="category-ac-chip">
              {opt.name}
              {!disabled && (
                <button type="button" onClick={() => remove(opt.id)}>
                  <FaTimes />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      <div className={`category-ac-control ${disabled ? "disabled" : ""}`}>
        <FaSearch className="category-ac-icon" />
        <input
          type="text"
          disabled={disabled}
          placeholder={disabled ? emptyMessage : placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          autoComplete="off"
        />
      </div>

      {open && !disabled && (
        <div className="category-ac-menu">
          {options.length === 0 ? (
            <div className="category-ac-empty">{emptyMessage}</div>
          ) : filtered.length === 0 ? (
            <div className="category-ac-empty">No matching subcategories</div>
          ) : (
            filtered.map((opt) => (
              <button
                type="button"
                key={opt.id}
                className="category-ac-option"
                onClick={() => add(opt.id)}
              >
                <span
                  className="category-ac-name"
                  style={{ paddingLeft: `${(opt.depth || 0) * 14}px` }}
                >
                  {opt.name}
                </span>
                {opt.depth > 0 && opt.path?.length > 1 && (
                  <small className="category-ac-path">
                    {opt.path.slice(0, -1).join(" › ")}
                  </small>
                )}
              </button>
            ))
          )}
        </div>
      )}
      {helpText && <Form.Text>{helpText}</Form.Text>}
    </Form.Group>
  );
}
